import React, { useCallback } from 'react';
import agent from '../../API/apiCalls';
import { GlobalResponseModel, IGetEscalatedDataPayload } from '../../Models/applicationModels';
import { IAvailableCreatedBy, IAvailableEscalation, IAvailableEscalationStatus, IGetEscalatedDataResponse, IPaginationDetailModel } from '../../Models/escalatedPageModels';
import { UserContext, UserPayload } from '../../Context/userContext';
import { useEscalationInfo } from '../../Context/escalationContext';
import { toast } from 'react-toastify';
import { shouldShowErrorToast } from '../../utils/apiRequestError';

export function useEscalatedDataFetch() {
    const { setShowLoader } = React.useContext(UserContext) as UserPayload;
    const { setEscalatedData, setEscalationStatusesCount, setTotalEscalationsCount, setPaginationDetail, setEscalationList, setEscalationStatusList, setCreatedByList } = useEscalationInfo();

    const fetchEscalatedData = useCallback(
        async (payload: IGetEscalatedDataPayload) => {
            setShowLoader(true);
            try {
                const res: GlobalResponseModel<IGetEscalatedDataResponse> =
                    await agent.GetEscalatedData.post(payload);

                if (!res.flagStatus || res.isAxiosError || res.error) {
                    throw new Error(JSON.stringify(res));
                }

                setEscalatedData(res.value.escalatedApplications);
                setEscalationStatusesCount(res.value.escalationStatusesCount);
                setTotalEscalationsCount(res.value.totalEscalations);
                setPaginationDetail((prevState: IPaginationDetailModel) => ({
                    ...prevState,
                    count: res.value.totalEscalations ?? 0
                }));
                return res.value;
            } catch (err) {
                console.error("Error fetching escalated data:", err);
                if (shouldShowErrorToast(err)) {
                    toast.error("Error fetching escalated data");
                }
            } finally {
                setShowLoader(false);
            }
        },
        []
    );

    const fetchEscalationList = useCallback(
        async () => {
            setShowLoader(true);
            try {
                const res: GlobalResponseModel<IAvailableEscalation[]> =
                    await agent.GenericEscalationTypes.get();

                if (!res.flagStatus || res.isAxiosError || res.error) {
                    throw new Error(JSON.stringify(res));
                }

                setEscalationList(res.value);
                return res.value;
            } catch (err) {
                console.error("Error fetching escalation list:", err);
                if (shouldShowErrorToast(err)) {
                    toast.error("Error fetching escalation list");
                }
            } finally {
                setShowLoader(false);
            }
        },
        []
    );

    const fetchEscalationStatusList = useCallback(
        async () => {
            setShowLoader(true);
            try {
                const res: GlobalResponseModel<IAvailableEscalationStatus[]> =
                    await agent.GenericEscalationStatuses.get();

                if (!res.flagStatus || res.isAxiosError || res.error) {
                    throw new Error(JSON.stringify(res));
                }

                setEscalationStatusList(res.value);
                return res.value;
            } catch (err) {
                console.error("Error fetching escalation status list:", err);
                if (shouldShowErrorToast(err)) {
                    toast.error("Error fetching escalation status list");
                }
            } finally {
                setShowLoader(false);
            }
        },
        []
    );

    const fetchCreatedByList = useCallback(
        async () => {
            setShowLoader(true);
            try {
                const res: GlobalResponseModel<IAvailableCreatedBy[]> =
                    await agent.GenericDevPortalUsers.get();

                if (!res.flagStatus || res.isAxiosError || res.error) {
                    throw new Error(JSON.stringify(res));
                }

                setCreatedByList(res.value);
                return res.value;
            } catch (err) {
                console.error("Error fetching created by list:", err);
                if (shouldShowErrorToast(err)) {
                    toast.error("Error fetching created by list");
                }
            } finally {
                setShowLoader(false);
            }
        },
        []
    );

    return { fetchEscalatedData, fetchEscalationList, fetchEscalationStatusList, fetchCreatedByList };
}
